import React from "react";
import { GroupCount } from "../../api/Items";
import LabeledCheckbox from "../../shared-components/LabeledCheckbox";
import { classNames } from "../../utils";
import { SummaryFilterTypeEnum } from "./SidebarSummary";
import { KeywordGroupExternal } from "../../api/Blueprints";
import {
  SidebarFilterGroup,
  SidebarGroupedFilters,
} from "./SidebarGroupedFilters";
import { countByName, FilterCount } from "./sidebarFilterCount";

type Props = {
  keywords?: string[];
  keywordCount?: GroupCount;
  filteredKeywordCount?: GroupCount;
  filtersActive?: boolean;
  applyFilterByType?: (
    type: SummaryFilterTypeEnum,
    event: React.ChangeEvent<HTMLInputElement>,
    source: string
  ) => void;
  hasCheckbox: boolean;
  onSelectionChange?: (key: string, value: boolean) => void;
  selections?: string[];
  keywordGroups?: KeywordGroupExternal[];
};

export const SidebarKeywords = ({
  keywordCount,
  filteredKeywordCount,
  filtersActive = false,
  applyFilterByType,
  hasCheckbox = false,
  onSelectionChange,
  selections,
  keywordGroups,
}: Props) => {
  const showFiltered = filtersActive && !!filteredKeywordCount;
  let keywordsOptions: string[] = [];

  if (keywordCount) {
    const newKeywords: string[] = [];
    keywordCount?.groups.forEach((keyword) => {
      if (!newKeywords.includes(keyword.name)) {
        newKeywords.push(keyword.name);
      }
    });
    keywordsOptions = newKeywords;
  }

  const countByKeyword = new Map(
    (keywordCount?.groups ?? []).map((keyword) => [
      keyword.name.toLowerCase(),
      keyword,
    ])
  );
  const groupedKeywords: SidebarFilterGroup[] = (keywordGroups ?? [])
    .map((group) => {
      const seen = new Set<string>();
      const children = [...group.include_exact, ...group.include_fuzzy]
        .map((word) => {
          const count = countByKeyword.get(word.toLowerCase());
          if (!count || seen.has(word.toLowerCase())) return null;
          seen.add(word.toLowerCase());
          const filtered = countByName(
            filteredKeywordCount?.groups,
            count.name
          );
          return {
            id: `${group.external_id}-${word.toLowerCase()}`,
            label: count.name,
            value: count.name.toLowerCase(),
            count: showFiltered ? filtered ?? 0 : count.count,
            baselineCount: showFiltered ? count.count : undefined,
          };
        })
        .filter((child): child is NonNullable<typeof child> => child !== null);
      return {
        id: group.external_id,
        name: group.name,
        children,
      };
    })
    .filter((group) => group.children.length > 0);

  const showGroups = hasCheckbox && groupedKeywords.length > 0;
  const groupedValues = new Set(
    groupedKeywords.flatMap((group) =>
      group.children.map((child) => child.value)
    )
  );
  // Words the behavior kit does not list in any group still have counts, so they
  // keep their flat rows underneath the groups instead of dropping out.
  const ungroupedKeywords = showGroups
    ? keywordsOptions.filter(
        (keyword) => !groupedValues.has(keyword.toLowerCase())
      )
    : keywordsOptions;

  return (
    <div>
      {showGroups && (
        <SidebarGroupedFilters
          groups={groupedKeywords}
          selectedValues={selections ?? []}
          onChange={(event, values) =>
            values.forEach((value) => {
              applyFilterByType?.(SummaryFilterTypeEnum.keyword, event, value);
              onSelectionChange?.(value, event.target.checked);
            })
          }
        />
      )}
      {ungroupedKeywords.sort().map((keyword: string) => {
        const baseline =
          countByName(keywordCount?.groups, keyword) ?? 0;
        const current = showFiltered
          ? countByName(filteredKeywordCount?.groups, keyword) ?? 0
          : baseline;
        keyword = keyword
          .toLowerCase()
          .replace(/\b(\w)/g, (s) => s.toUpperCase());
        return (
          <div
            className="flex gap-1 capitalize items-start text-sm mb-1 font-medium"
            key={keyword}
          >
            {hasCheckbox ? (
              <>
                {selections ? (
                  <LabeledCheckbox
                    classes="mt-0.5 shrink-0"
                    name={keyword}
                    onChange={(e) => {
                      applyFilterByType?.(
                        SummaryFilterTypeEnum.keyword,
                        e,
                        keyword.toLowerCase()
                      );
                      onSelectionChange?.(
                        keyword.toLowerCase(),
                        e.target.checked
                      );
                    }}
                    checked={selections.some(
                      (selection) =>
                        selection.toLowerCase() === keyword.toLowerCase()
                    )}
                  ></LabeledCheckbox>
                ) : (
                  <LabeledCheckbox
                    classes="mt-0.5 shrink-0"
                    name={keyword}
                    onChange={(e) => {
                      applyFilterByType?.(
                        SummaryFilterTypeEnum.keyword,
                        e,
                        keyword.toLowerCase()
                      );
                      onSelectionChange?.(
                        keyword.toLowerCase(),
                        e.target.checked
                      );
                    }}
                  ></LabeledCheckbox>
                )}
              </>
            ) : (
              <Dot />
            )}
            {keyword}{" "}
            <FilterCount
              current={current}
              total={baseline}
              showTotal={showFiltered}
            />
          </div>
        );
      })}
    </div>
  );
};

function Dot({ className }: { className?: string }) {
  return (
    <span
      className={classNames(
        "inline-block h-1 w-1 mr-2 rounded-full",
        className ?? "bg-gray-500"
      )}
    ></span>
  );
}
